import React, { useMemo, useState } from "react";
import { IconCamera, IconTrash } from "@tabler/icons-react";
import Modal from "../../components/Modal/Modal";
import PhoneInput from "../../components/PhoneInput/PhoneInput";
import DentistAvatar from "../../components/DentistAvatar/DentistAvatar";
import { useClinicData } from "../../context/ClinicDataContext";
import { formatTime, initials, minutesToTime, timeToMinutes } from "../../utils/format";
import { DEFAULT_COUNTRY_CODE, hasNumber, joinPhone, splitPhone } from "../../utils/phone";

export const WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const DENTIST_COLORS = ["#047fbb", "#15448a", "#7a4fb5", "#b5532a", "#13804f", "#c2410c", "#be185d", "#0e7490"];

const splitList = (text) =>
  text
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const SECTIONS = [
  { id: "profile", label: "Profile" },
  { id: "schedule", label: "Working hours" },
  { id: "booking", label: "Online booking" },
];

// Add or edit a dentist. Profile fields are what patients see on the patient portal.
const DentistFormModal = ({ dentist, onClose, onSaved }) => {
  const { clinic, treatments, saveDentist } = useClinicData();
  const isEdit = Boolean(dentist?.id);
  const [section, setSection] = useState("profile");
  const [error, setError] = useState("");
  const [form, setForm] = useState(() => ({
    name: dentist?.name || "",
    role: dentist?.role || "",
    qualifications: dentist?.qualifications || "",
    experienceYears: dentist?.experienceYears ?? "",
    languages: (dentist?.languages || []).join(", "),
    focus: (dentist?.focus || []).join(", "),
    bio: dentist?.bio || "",
    fee: dentist?.fee ?? "",
    phone: dentist?.phone || DEFAULT_COUNTRY_CODE,
    color: dentist?.color || DENTIST_COLORS[0],
    photoUrl: dentist?.photoUrl || "",
    days: dentist?.days || clinic.openDays.filter((d) => d !== "Sat"),
    start: dentist?.start || clinic.openTime,
    end: dentist?.end || clinic.closeTime,
    breakStart: dentist?.breakStart || "",
    breakEnd: dentist?.breakEnd || "",
    online: dentist?.online ?? true,
    treatmentIds: dentist?.treatmentIds || [],
  }));

  const set = (field) => (value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setError("");
  };
  const onInput = (field) => (e) => set(field)(e.target.value);

  // Half-hour steps across opening hours, including closing time as an end option.
  const times = useMemo(() => {
    const out = [];
    for (let m = timeToMinutes(clinic.openTime); m <= timeToMinutes(clinic.closeTime); m += clinic.slotMinutes) {
      out.push(minutesToTime(m));
    }
    return out;
  }, [clinic]);

  const categories = useMemo(() => [...new Set(treatments.map((t) => t.category))], [treatments]);
  const allTreatments = form.treatmentIds.length === 0;

  const toggleDay = (day) =>
    set("days")(form.days.includes(day) ? form.days.filter((d) => d !== day) : WEEK.filter((d) => d === day || form.days.includes(d)));

  const toggleTreatment = (id) => {
    const current = allTreatments ? treatments.map((t) => t.id) : form.treatmentIds;
    const next = current.includes(id) ? current.filter((t) => t !== id) : [...current, id];
    if (!next.length) {
      setError("A dentist needs at least one treatment.");
      return;
    }
    set("treatmentIds")(next.length === treatments.length ? [] : next);
  };

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setError("Photo must be smaller than 2 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("photoUrl")(reader.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    if (!form.name.trim() || !form.role.trim()) return ["profile", "Enter the dentist's name and role."];
    if (!hasNumber(form.phone)) return ["profile", "Enter the dentist's mobile number."];
    if (!form.days.length) return ["schedule", "Pick at least one working day."];
    if (timeToMinutes(form.end) <= timeToMinutes(form.start)) return ["schedule", "Working hours must end after they start."];
    if (Boolean(form.breakStart) !== Boolean(form.breakEnd)) return ["schedule", "Set both the break start and end, or leave both empty."];
    if (form.breakStart) {
      const bs = timeToMinutes(form.breakStart);
      const be = timeToMinutes(form.breakEnd);
      if (be <= bs || bs < timeToMinutes(form.start) || be > timeToMinutes(form.end)) {
        return ["schedule", "The break must fall inside working hours and end after it starts."];
      }
    }
    if (form.fee !== "" && (Number.isNaN(Number(form.fee)) || Number(form.fee) < 0)) return ["profile", "Consultation fee must be a number."];
    return null;
  };

  const handleSave = () => {
    const problem = validate();
    if (problem) {
      setSection(problem[0]);
      setError(problem[1]);
      return;
    }
    const { code, number } = splitPhone(form.phone);
    const saved = saveDentist({
      ...(dentist || {}),
      ...form,
      name: form.name.trim(),
      role: form.role.trim(),
      phone: joinPhone(code, number),
      languages: splitList(form.languages),
      focus: splitList(form.focus),
      experienceYears: form.experienceYears === "" ? "" : Number(form.experienceYears),
      fee: form.fee === "" ? "" : Number(form.fee),
    });
    onSaved?.(saved);
    onClose();
  };

  const preview = { ...form, initials: initials(form.name || "?") };

  return (
    <Modal
      title={isEdit ? `Edit ${dentist.name}` : "Add dentist"}
      onClose={onClose}
      width={720}
      footer={
        <>
          <button className="btn btn--secondary" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn--primary" type="button" onClick={handleSave}>
            {isEdit ? "Save changes" : "Add dentist"}
          </button>
        </>
      }
    >
      <div className="tabs dentist-form__tabs" role="tablist" aria-label="Dentist details">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={section === s.id}
            className={`tabs__item ${section === s.id ? "tabs__item--active" : ""}`}
            onClick={() => setSection(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === "profile" && (
        <div className="form-grid">
          <div className="dentist-form__identity form-grid__full">
            <DentistAvatar dentist={preview} size="lg" />
            <div className="dentist-form__photo-actions">
              <label className="btn btn--secondary btn--sm">
                <IconCamera size={16} stroke={1.75} />
                {form.photoUrl ? "Change photo" : "Upload photo"}
                <input className="visually-hidden" type="file" accept="image/*" onChange={onPhoto} />
              </label>
              {form.photoUrl && (
                <button className="btn btn--danger btn--sm" type="button" onClick={() => set("photoUrl")("")}>
                  <IconTrash size={16} stroke={1.75} />
                  Remove
                </button>
              )}
              <span className="form-field__hint">Shown on the patient portal. Square photo, under 2 MB.</span>
            </div>
          </div>

          <label className="form-field">
            <span className="form-field__label">Full name</span>
            <input className="form-field__control" value={form.name} onChange={onInput("name")} placeholder="e.g. Dr. Lim Wei Jie" />
          </label>
          <label className="form-field">
            <span className="form-field__label">Role</span>
            <input className="form-field__control" value={form.role} onChange={onInput("role")} placeholder="e.g. General dentist" />
          </label>
          <label className="form-field">
            <span className="form-field__label">Qualifications (optional)</span>
            <input className="form-field__control" value={form.qualifications} onChange={onInput("qualifications")} placeholder="e.g. BDS (Malaya)" />
          </label>
          <label className="form-field">
            <span className="form-field__label">Years of experience (optional)</span>
            <input className="form-field__control" type="number" min="0" value={form.experienceYears} onChange={onInput("experienceYears")} />
          </label>
          <label className="form-field">
            <span className="form-field__label">Languages (optional)</span>
            <input className="form-field__control" value={form.languages} onChange={onInput("languages")} placeholder="English, Malay, Mandarin" />
            <span className="form-field__hint">Separate with commas.</span>
          </label>
          <label className="form-field">
            <span className="form-field__label">Areas of focus (optional)</span>
            <input className="form-field__control" value={form.focus} onChange={onInput("focus")} placeholder="Fillings, Root canal, Crowns" />
            <span className="form-field__hint">Separate with commas.</span>
          </label>
          <label className="form-field form-grid__full">
            <span className="form-field__label">About (optional)</span>
            <textarea className="form-field__control" value={form.bio} onChange={onInput("bio")} placeholder="A short introduction patients will read before booking." />
          </label>
          <label className="form-field">
            <span className="form-field__label">Consultation fee, RM (optional)</span>
            <input className="form-field__control" type="number" min="0" step="1" value={form.fee} onChange={onInput("fee")} />
          </label>
          <div>
            <PhoneInput label="Mobile number" value={form.phone} onChange={set("phone")} />
          </div>
          <div className="form-field form-grid__full">
            <span className="form-field__label">Calendar colour</span>
            <div className="dentist-form__colors" role="radiogroup" aria-label="Calendar colour">
              {DENTIST_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={form.color === c}
                  aria-label={c}
                  className={`dentist-form__color ${form.color === c ? "dentist-form__color--active" : ""}`}
                  style={{ background: c }}
                  onClick={() => set("color")(c)}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {section === "schedule" && (
        <div className="form-grid">
          <div className="form-field form-grid__full">
            <span className="form-field__label">Working days</span>
            <div className="dentist-form__days">
              {WEEK.map((day) => {
                const closed = !clinic.openDays.includes(day);
                const on = form.days.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={on}
                    disabled={closed}
                    title={closed ? "Clinic closed" : undefined}
                    className={`dentist-form__day ${on ? "dentist-form__day--on" : ""}`}
                    style={on ? { background: form.color, borderColor: form.color } : undefined}
                    onClick={() => toggleDay(day)}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <span className="form-field__hint">Days the clinic is closed can't be selected.</span>
          </div>
          <label className="form-field">
            <span className="form-field__label">Starts</span>
            <select className="form-field__control" value={form.start} onChange={onInput("start")}>
              {times.slice(0, -1).map((t) => (
                <option key={t} value={t}>{formatTime(t)}</option>
              ))}
            </select>
          </label>
          <label className="form-field">
            <span className="form-field__label">Ends</span>
            <select className="form-field__control" value={form.end} onChange={onInput("end")}>
              {times.slice(1).map((t) => (
                <option key={t} value={t}>{formatTime(t)}</option>
              ))}
            </select>
          </label>
          <label className="form-field">
            <span className="form-field__label">Break starts (optional)</span>
            <select className="form-field__control" value={form.breakStart} onChange={onInput("breakStart")}>
              <option value="">No break</option>
              {times.slice(1, -1).map((t) => (
                <option key={t} value={t}>{formatTime(t)}</option>
              ))}
            </select>
          </label>
          <label className="form-field">
            <span className="form-field__label">Break ends</span>
            <select className="form-field__control" value={form.breakEnd} onChange={onInput("breakEnd")} disabled={!form.breakStart}>
              <option value="">—</option>
              {times.slice(1, -1).map((t) => (
                <option key={t} value={t}>{formatTime(t)}</option>
              ))}
            </select>
          </label>
          <p className="form-field__hint form-grid__full">
            Patients can only book online inside these hours. Use Schedule &amp; leave on the dentist's card for days off.
          </p>
        </div>
      )}

      {section === "booking" && (
        <div className="dentist-form__booking">
          <label className="dentist-form__switch">
            <input type="checkbox" checked={form.online} onChange={(e) => set("online")(e.target.checked)} />
            <span>
              <strong>Show on patient portal and accept online bookings</strong>
              <span>Turn off to keep this dentist for front-desk bookings only.</span>
            </span>
          </label>

          <div className="form-field">
            <span className="form-field__label">Treatments this dentist performs</span>
            <span className="form-field__hint">
              {allTreatments ? "All treatments are selected." : `${form.treatmentIds.length} of ${treatments.length} selected.`} Patients
              booking online only see this dentist for these treatments.
            </span>
          </div>
          {categories.map((cat) => (
            <fieldset className="dentist-form__group" key={cat}>
              <legend>{cat}</legend>
              {treatments
                .filter((t) => t.category === cat)
                .map((t) => (
                  <label className="dentist-form__check" key={t.id}>
                    <input
                      type="checkbox"
                      checked={allTreatments || form.treatmentIds.includes(t.id)}
                      onChange={() => toggleTreatment(t.id)}
                    />
                    {t.name}
                  </label>
                ))}
            </fieldset>
          ))}
        </div>
      )}

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
};

export default DentistFormModal;
